import { supabase } from "../../config/supabase";
import { Crop } from "./crops";

export interface Sector {
  id: string;
  company_id: string;
  crop_id: string;
  sector_name: string;
  length_m: number;
  width_m: number;
  distance_supply_rows_m: number;
  distance_suppliers_cm: number;
  nominal_flow_rate_lh: number;
  ferti_flow_rate_lh: number;
  pipe_length_m: number;
  created_at?: string;
  updated_at?: string;
  crops?: Crop;
}

interface SectorData {
  sector_name: string;
  company_id: string;
  crop_id: string;
  length_m?: number;
  width_m?: number;
  distance_supply_row?: number;
  distance_suppliers_cm?: number;
  nominal_flow_rate_lh?: number;
  ferti_flow_rate_lh?: number;
  pipe_length_m?: number;
}

interface ServiceResponse<T> {
  success: boolean;
  data?: T;
  error?: any;
}

export class SectorService {
  async listSectors() {
    try {
      // Get current user for RLS policy
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("sectors")
        .select("*")
        .eq("user_id", user.id)
        .order("sector_name", { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: (data as Sector[]).map((sector) => ({
          id: sector.id,
          sector_name: sector.sector_name,
          company_id: sector.company_id,
          crop_id: sector.crop_id,
          length_m: sector.length_m || 0,
          width_m: sector.width_m || 0,
          distance_supply_row: sector.distance_supply_rows_m || 0,
          distance_suppliers_cm: sector.distance_suppliers_cm || 0,
          nominal_flow_rate_lh: sector.nominal_flow_rate_lh || 0,
          ferti_flow_rate_lh: sector.ferti_flow_rate_lh || 0,
          pipe_length_m: sector.pipe_length_m || 0,
          area_mq: (sector.length_m || 0) * (sector.width_m || 0), // calculated area
        })),
      };
    } catch (error) {
      console.error("Error listing sectors:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: [],
      };
    }
  }

  async createSector(sectorData: SectorData) {
    try {
      // Get current user for RLS policy
      const {
        data: { user },
        error: userError,
      } = await supabase.auth.getUser();

      if (userError || !user) {
        throw new Error("User not authenticated");
      }

      const { data, error } = await supabase
        .from("sectors")
        .insert({
          sector_name: sectorData.sector_name,
          company_id: sectorData.company_id,
          crop_id: sectorData.crop_id,
          length_m: sectorData.length_m || 0,
          width_m: sectorData.width_m || 0,
          distance_supply_rows_m: sectorData.distance_supply_row || 0,
          distance_suppliers_cm: sectorData.distance_suppliers_cm || 0,
          nominal_flow_rate_lh: sectorData.nominal_flow_rate_lh || 0,
          ferti_flow_rate_lh: sectorData.ferti_flow_rate_lh || 0,
          pipe_length_m: sectorData.pipe_length_m || 0,
          user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: (data as Sector).id,
          sector_name: (data as Sector).sector_name,
          company_id: (data as Sector).company_id,
          crop_id: (data as Sector).crop_id,
          length_m: (data as Sector).length_m,
          width_m: (data as Sector).width_m,
          distance_supply_row: (data as Sector).distance_supply_rows_m,
          distance_suppliers_cm: (data as Sector).distance_suppliers_cm,
          nominal_flow_rate_lh: (data as Sector).nominal_flow_rate_lh,
          ferti_flow_rate_lh: (data as Sector).ferti_flow_rate_lh,
          pipe_length_m: (data as Sector).pipe_length_m,
          area_mq:
            ((data as Sector).length_m || 0) * ((data as Sector).width_m || 0),
        },
      };
    } catch (error) {
      console.error("Error creating sector:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: null,
      };
    }
  }

  async updateSector(id: string, sectorData: SectorData) {
    try {
      const { data, error } = await supabase
        .from("sectors")
        .update({
          sector_name: sectorData.sector_name,
          company_id: sectorData.company_id,
          crop_id: sectorData.crop_id,
          length_m: sectorData.length_m || 0,
          width_m: sectorData.width_m || 0,
          distance_supply_rows_m: sectorData.distance_supply_row || 0,
          distance_suppliers_cm: sectorData.distance_suppliers_cm || 0,
          nominal_flow_rate_lh: sectorData.nominal_flow_rate_lh || 0,
          ferti_flow_rate_lh: sectorData.ferti_flow_rate_lh || 0,
          pipe_length_m: sectorData.pipe_length_m || 0,
        })
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;

      return {
        success: true,
        data: {
          id: (data as Sector).id,
          sector_name: (data as Sector).sector_name,
          company_id: (data as Sector).company_id,
          crop_id: (data as Sector).crop_id,
          length_m: (data as Sector).length_m,
          width_m: (data as Sector).width_m,
          distance_supply_row: (data as Sector).distance_supply_rows_m,
          distance_suppliers_cm: (data as Sector).distance_suppliers_cm,
          nominal_flow_rate_lh: (data as Sector).nominal_flow_rate_lh,
          ferti_flow_rate_lh: (data as Sector).ferti_flow_rate_lh,
          pipe_length_m: (data as Sector).pipe_length_m,
          area_mq:
            ((data as Sector).length_m || 0) * ((data as Sector).width_m || 0),
        },
      };
    } catch (error) {
      console.error("Error updating sector:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
        data: null,
      };
    }
  }

  async deleteSector(id: string) {
    try {
      const { error } = await supabase.from("sectors").delete().eq("id", id);

      if (error) throw error;

      return {
        success: true,
      };
    } catch (error) {
      console.error("Error deleting sector:", (error as Error).message);
      return {
        success: false,
        error: (error as Error).message,
      };
    }
  }

  async getSectorsByUserId(userId: string) {
    try {
      const { data, error } = await supabase
        .from("sectors")
        .select("*")
        .eq("user_id", userId)
        .order("sector_name", { ascending: true });

      if (error) throw error;

      return {
        success: true,
        data: (data as Sector[]).map((sector) => ({
          id: sector.id,
          sector_name: sector.sector_name,
          company_id: sector.company_id,
          crop_id: sector.crop_id,
          length_m: sector.length_m,
          width_m: sector.width_m,
          distance_supply_row: sector.distance_supply_rows_m,
          distance_suppliers_cm: sector.distance_suppliers_cm,
          nominal_flow_rate_lh: sector.nominal_flow_rate_lh,
          ferti_flow_rate_lh: sector.ferti_flow_rate_lh,
          pipe_length_m: sector.pipe_length_m,
          area_mq: (sector.length_m || 0) * (sector.width_m || 0),
        })),
      };
    } catch (error) {
      console.error(
        "Error getting sectors by user ID:",
        (error as Error).message
      );
      return {
        success: false,
        error: (error as Error).message,
        data: [],
      };
    }
  }

  async listSectorsByCompany(
    companyId: string
  ): Promise<ServiceResponse<Sector[]>> {
    try {
      const { data, error } = await supabase
        .from("sectors")
        .select(
          `
          id,
          company_id,
          crop_id,
          sector_name,
          length_m,
          width_m,
          distance_supply_rows_m,
          distance_suppliers_cm,
          nominal_flow_rate_lh,
          ferti_flow_rate_lh,
          pipe_length_m,
          crops (
            id,
            crop_name,
            nitrogen_n,
            phosphorus_p2o5,
            potassium_k2o,
            calcium_cao,
            magnesium_mgo,
            sulfur_so3
          )
        `
        )
        .eq("company_id", companyId);

      if (error) throw error;

      // Transform the data to match our interface
      const transformedData = data?.map((sector) => ({
        ...sector,
        crops: sector.crops?.[0], // Take first crop since it's a one-to-one relationship
      })) as Sector[];

      return { success: true, data: transformedData };
    } catch (error) {
      console.error("Error fetching sectors:", error);
      return { success: false, error };
    }
  }

  async getSectorWithCrop(sectorId: string): Promise<ServiceResponse<Sector>> {
    try {
      const { data, error } = await supabase
        .from("sectors")
        .select(
          `
          *,
          crops!inner (
            id,
            crop_name,
            nitrogen_n,
            phosphorus_p2o5,
            potassium_k2o,
            calcium_cao,
            magnesium_mgo,
            sulfur_so3
          )
        `
        )
        .eq("id", sectorId)
        .single();

      if (error) throw error;

      if (!data) {
        throw new Error("Sector not found");
      }

      // Transform the data to match our interface
      const transformedData: Sector = {
        ...data,
        crops: data.crops, // Direct assignment since we're using !inner join
      };

      return { success: true, data: transformedData };
    } catch (error) {
      console.error("Error fetching sector:", error);
      return { success: false, error };
    }
  }

  async existsSectorByName(sector_name: string) {
    try {
      const { data, error } = await supabase
        .from("sectors")
        .select("id")
        .ilike("sector_name", sector_name);
      if (error) {
        console.error("Error checking sector by name:", error);
        return { success: false, error };
      }
      return { success: true, exists: Array.isArray(data) && data.length > 0 };
    } catch (error) {
      console.error("Error in existsSectorByName:", error);
      return { success: false, error };
    }
  }
}

export const sectorService = new SectorService();
